import { useEffect, useState } from 'react';
import { View } from 'react-native';
import { Button, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { getUserDetails, getDriverWorkContext, type DriverWorkContext, type UserDetails } from '../api/apiService';
import { workContextLicenseLabel } from './workContextLabels';

/** Authenticated display only. Never send identity fields or raw errors to telemetry. */
export function DriverSidebarHeader({ refreshKey }: { refreshKey: string }) {
  const { t } = useTranslation('workCompany');
  const theme = useTheme();
  const [user, setUser] = useState<UserDetails | null>(null);
  const [context, setContext] = useState<DriverWorkContext | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [contextLoading, setContextLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let live = true;
    setUser(null);
    setContext(null);
    setFailed(false);
    setProfileLoading(true);
    setContextLoading(true);
    void getUserDetails().then(response => {
      if (!live) return;
      if (response.ok && response.data) setUser(response.data);
      else setFailed(true);
    }).catch(() => { if (live) setFailed(true); })
      .finally(() => { if (live) setProfileLoading(false); });
    void getDriverWorkContext().then(response => {
      if (!live) return;
      if (response.ok && response.data) setContext(response.data);
      else setFailed(true);
    }).catch(() => { if (live) setFailed(true); })
      .finally(() => { if (live) setContextLoading(false); });
    return () => { live = false; };
  }, [refreshKey, retry]);

  const unknown = t('unknownLicense', 'To confirm');
  const session = context?.open_session_context;
  // The API has no historical fiscal identifier. Never borrow a destination's ID.
  const company = session
    ? session.company_name || t('historicalCompanyMissing', 'No company recorded for this open session')
    : context?.work_company?.name || unknown;
  const fiscalCompany = !session || (session.company_id !== null && session.company_id === context?.work_company?.id)
    ? context?.work_company : null;
  const identifierLabel = fiscalCompany?.tax_identifier_kind === 'nif' ? t('driverNif', 'NIF')
    : fiscalCompany?.tax_identifier_kind === 'nipc' ? t('companyNipc', 'NIPC')
      : t('companyTaxId', 'Company tax ID');

  return <View testID="driver-sidebar-header" style={{ paddingHorizontal: 16, paddingBottom: 16, gap: 4 }}>
    <Text testID="driver-sidebar-name" variant="titleSmall" style={{ color: theme.colors.onSurface, flexShrink: 1 }}>
      {user?.full_name || (profileLoading ? t('profileLoading', 'Loading profile') : unknown)}
    </Text>
    <Text testID="driver-sidebar-nif" variant="bodySmall">{t('driverNif', 'NIF')}: {user?.profile?.nif || unknown}</Text>
    {contextLoading ? <Text testID="driver-sidebar-loading" variant="bodySmall">{t('loading', 'Loading work context')}</Text>
      : context && <>
        <Text testID="driver-sidebar-company" variant="bodySmall">{t('company', 'Company')}: {company}</Text>
        <Text testID="driver-sidebar-company-tax-id" variant="bodySmall">{identifierLabel}: {fiscalCompany?.tax_identifier || unknown}</Text>
        <Text testID="driver-sidebar-license" variant="bodySmall">{t('license', 'License')}: {workContextLicenseLabel(context, t)}</Text>
      </>}
    {failed && <>
      <Text testID="driver-sidebar-error" accessibilityRole="alert" variant="bodySmall">{t('sidebarLoadError', 'Unable to load profile or work context. Retry or contact support.')}</Text>
      <Button compact testID="driver-sidebar-retry" onPress={() => setRetry(value => value + 1)}>{t('retryAction', 'Retry')}</Button>
    </>}
  </View>;
}
